import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { notification } from 'tea-component';
import {
  getInDevFinancialsByMonth,
  getInDevFinancialsByPhase,
} from '@sg/services';
import {
  createBtnPoint,
  InDevProjectsTab,
  SGBuriedBtnPoint,
} from '@/pages/strategic/utils/buriedPoint';
import { DefaultFinancialsChat } from '../../../../../pageConfig';
import type { TInDevChartViewKey } from '../../../../../hooks/useInDevChartEyesState';

interface IUseHeadcountRampDataParams {
  projectId: string;
  selectedMonth?: string | null;
  activeDateTypeSegment: string;
  projectInfo: string;
  viewEnabled: boolean;
  updateInDevEyesViewState: (viewKey: TInDevChartViewKey, enabled: boolean) => void;
}

export const useHeadcountRampData = ({
  projectId,
  selectedMonth,
  activeDateTypeSegment,
  projectInfo,
  viewEnabled,
  updateInDevEyesViewState,
}: IUseHeadcountRampDataParams) => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [updateTime, setUpdateTime] = useState<string>('');
  const [showChatsType, setShowChatsType] = useState<any>(DefaultFinancialsChat);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setShowChatsType((prev: any) => ({
      ...prev,
      format: viewEnabled,
    }));
  }, [viewEnabled]);

  const handleChatsTypeChange = useCallback(
    (e: any, downloadValue?: string) => {
      const currentViewEnabled = !!viewEnabled;
      if (downloadValue === 'download') {
        const btnPoint = createBtnPoint(
          InDevProjectsTab,
          'headcountRampDownload',
          `Headcount Ramp - Download - ${projectInfo}`,
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      } else if (e?.value && e.value !== 'download') {
        const value = e.value;
        const isViewClick = value === 'view'
          || (
            e.format !== showChatsType?.format
            && value === showChatsType?.value
          );

        if (isViewClick) {
          const nextViewEnabled = !currentViewEnabled;
          const btnPoint = createBtnPoint(
            InDevProjectsTab,
            'headcountRampView',
            `Headcount Ramp - View - ${nextViewEnabled ? 'On' : 'Off'} - ${projectInfo}`,
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
          updateInDevEyesViewState('headcountRamp', nextViewEnabled);
          setShowChatsType((prev: any) => ({
            ...prev,
            format: nextViewEnabled,
          }));
          return;
        }

        if (value === 'bar') {
          if (showChatsType?.value !== 'bar') {
            const btnPoint = createBtnPoint(
              InDevProjectsTab,
              'headcountRampBarChart',
              `Headcount Ramp - Bar Chart - ${projectInfo}`,
            );
            if (btnPoint) {
              SGBuriedBtnPoint(btnPoint);
            }
          }
        } else if (value === 'table') {
          if (showChatsType?.value !== 'table') {
            const btnPoint = createBtnPoint(
              InDevProjectsTab,
              'headcountRampTable',
              `Headcount Ramp - Table - ${projectInfo}`,
            );
            if (btnPoint) {
              SGBuriedBtnPoint(btnPoint);
            }
          }
        }
      }

      setShowChatsType({
        ...e,
        format: currentViewEnabled,
      });
    },
    [projectInfo, showChatsType, viewEnabled, updateInDevEyesViewState],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let res: any;
      let nextTableData: any[] = [];

      if (activeDateTypeSegment === 'phase') {
        const payload: { project_id: string; datatime?: string } = {
          project_id: projectId,
        };
        if (selectedMonth) {
          payload.datatime = selectedMonth;
        }
        res = await getInDevFinancialsByPhase(payload);
        nextTableData =
          res?.data?.hc?.map((item: any) => ({
            date: item.milestone,
            total: parseFloat(item.metrics_value) || 0,
          })) || [];
      } else {
        const currentYear = new Date().getFullYear();
        const payload: {
          project_id: number;
          start_date: string;
          end_date: string;
          period: string;
          metrics: string[];
          logical_flag: string;
          datatime?: string;
        } = {
          project_id: parseInt(projectId, 10),
          start_date: `${currentYear}-01-01`,
          end_date: `${currentYear}-12-31`,
          period: 'month',
          metrics: ['r&d hc'],
          logical_flag: 'actual_else_forecast',
        };
        if (selectedMonth) {
          payload.datatime = selectedMonth;
        }
        res = await getInDevFinancialsByMonth(payload);
        nextTableData =
          res?.data?.map((item: any) => ({
            date: moment(item.date).format('YYYY-MM'),
            total: parseInt(item.value, 10) || 0,
          })) || [];
      }

      setTableData(nextTableData);
      const dataArray = activeDateTypeSegment === 'phase' ? res?.data?.hc : res?.data;
      const latestUpdateTime = dataArray?.[dataArray.length - 1]?.update_time;
      setUpdateTime(
        latestUpdateTime ? moment(latestUpdateTime).format('YYYY-MM') : '',
      );
    } catch (error) {
      console.error('Error fetching headcount data:', error);
      notification.error({
        description: 'Failed to fetch headcount data',
      });
      setTableData([]);
      setUpdateTime('');
    } finally {
      setLoading(false);
    }
  }, [activeDateTypeSegment, selectedMonth, projectId]);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [fetchData, projectId]);

  const chartData = useMemo(
    () => ({
      xAxisData: tableData.map((item: any) => item.date),
      seriesData: [
        {
          name: 'Total',
          type: 'bar',
          data: tableData.map((item: any) => ({
            value: item.total,
            tooltipWidth: activeDateTypeSegment === 'phase' ? '280px' : '200px',
            tooltipHeaderRight: 'Headcount',
            label: {
              position: item.total >= 0 ? 'top' : 'bottom',
            },
          })),
          color: '#7F6FF1',
          barWidth: '40%',
          showSymbol: showChatsType?.format,
          label: {
            show: !!showChatsType?.format,
            fontSize: 12,
            fontWeight: 'normal',
            color: '#525464',
            formatter: (params: any) => {
              const value = params?.value?.value ?? params?.value;
              if (value === null || value === undefined) return '';
              return value;
            },
          },
        },
      ],
    }),
    [tableData, showChatsType?.format, activeDateTypeSegment],
  );

  return {
    tableData,
    chartData,
    updateTime,
    loading,
    showChatsType,
    handleChatsTypeChange,
  };
};
